/**
 * Product 360 — everything we can match about one product, from every database,
 * with the source of every individual fact.
 *
 * The design rule here: there is ONE list of OEs (and one of crosses), not one
 * per database. Each row carries the sources that stated it, and the source chips
 * double as a filter — so "show me only the OEs TecDoc gave us" is a click, and
 * you never lose sight of the union.
 */
import { useState, type ReactNode } from 'react';
import FamilyPicker from '../components/FamilyPicker';
import ProductCustom from '../components/ProductCustom';
import ProductHits from '../components/ProductHits';
import ProductDocuments from '../components/ProductDocuments';
import ProductImages from '../components/ProductImages';
import SourceAssets from '../components/SourceAssets';
import { useApi } from '../lib/api';
import { useDrawer } from '../lib/drawer';
import { useResource } from '../lib/hooks';
import { useNav } from '../lib/router';
import type { CrossRelation, ProductAlias, ProductCustomValues, Provenance, ProductView } from '../lib/types';
import { fmtTime } from '../lib/util';
import {
  Banner,
  Chip,
  DataBlock,
  Empty,
  ErrorBox,
  Field,
  Loading,
  Modal,
  SourceChip,
  Switch,
  Table,
  Toolbar,
  cx,
} from '../ui';

const METHOD_HINT: Record<string, string> = {
  identity: 'same normalized brand + article',
  canonical_code: 'the catalog gave this as an alternate code for the same part',
  brand_alias: 'same brand under a different printed name (e.g. "VARTA (PAR)")',
  manual: 'fused by hand in the console',
  rule: 'fused by a curated rule',
};

const RELATION_HINT: Record<CrossRelation, string> = {
  equivalent: 'the same thing for cross purposes — this product inherits its whole cross list',
  interchange: 'they fit the same application, but each keeps its own cross list',
  supersedes: 'one replaces the other',
};

/** The little source buttons that appear on every fact. Clicking one filters the list. */
function Sources({
  sources,
  active,
  onPick,
}: {
  sources: Provenance[];
  active: string;
  onPick: (s: string) => void;
}) {
  const { show } = useDrawer();
  const seen = [...new Set(sources.map((s) => s.source))];
  return (
    <div className="flex flex-wrap gap-1">
      {seen.map((s) => {
        const ev = sources.filter((p) => p.source === s);
        return (
          <button
            key={s}
            title={`${s} — click to filter, shift-click for the evidence`}
            className={cx('cursor-pointer', active === s && 'ring-1 ring-accent rounded')}
            onClick={(e) => {
              if (e.shiftKey) {
                show({
                  title: `${s} — evidence`,
                  body: (
                    <pre className="mono overflow-auto rounded border border-line bg-panel2 p-3 text-[12px]">
                      {JSON.stringify(ev, null, 2)}
                    </pre>
                  ),
                });
              } else {
                onPick(active === s ? '' : s);
              }
            }}
          >
            <SourceChip source={s} />
          </button>
        );
      })}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 py-1 text-[12.5px]">
      <div className="w-28 shrink-0 text-faint">{label}</div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Attach another code this one product is printed under. */
function AddIdentity({ id, onClose, onDone }: { id: number; onClose: () => void; onDone: () => void }) {
  const { request } = useApi();
  const [form, setForm] = useState({ brand: '', article: '', name: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setErr('');
    const r = await request('POST', `/v1/admin/products/${id}/identities`, {
      brand: form.brand,
      article: form.article,
      ...(form.name.trim() ? { name: form.name } : {}),
    });
    setBusy(false);
    if (r.status >= 400) {
      setErr(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    onDone();
    onClose();
  }

  return (
    <Modal title="Add an identity" onClose={onClose}>
      <div className="flex flex-col gap-2.5">
        {err && <ErrorBox>{err}</ErrorBox>}
        <Field label="Brand">
          <input
            className="input mono"
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            autoFocus
          />
        </Field>
        <Field label="Article number">
          <input
            className="input mono"
            value={form.article}
            onChange={(e) => setForm({ ...form, article: e.target.value })}
          />
        </Field>
        <Field label="Name (optional)">
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <p className="text-[11.5px] text-faint">
          Another code this same physical part is printed under. If the code is already known, its product is{' '}
          <b>fused</b> into this one — a split undoes it, and either way it is recorded in the fusion log.
        </p>
        <div className="mt-1 flex justify-end gap-2">
          <button className="btn btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-sm btn-primary"
            disabled={busy || !form.brand.trim() || !form.article.trim()}
            onClick={save}
          >
            Add
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Identity, description and free-form attributes of a product we invented. */
function InternalDetails({ view, onChange }: { view: ProductView; onChange: () => void }) {
  const { request } = useApi();
  const p = view.product;
  const [form, setForm] = useState({
    brand: p.brandRaw ?? p.brandNorm,
    article: p.articleRaw ?? p.articleNorm,
    name: p.name ?? '',
    attrs: JSON.stringify(p.attrs ?? {}, null, 2),
  });
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  // A managed product's code is the catalogue's: shown, never rewritten.
  const managed = p.kind === 'mixed';

  // The family lives inside the attrs JSON — one place to store it, edited from
  // either the picker or the textarea, so neither can silently disagree with the
  // other about what the product actually carries.
  const familyOf = (json: string): number | null => {
    try {
      const v = (JSON.parse(json) as Record<string, unknown>).generic_article;
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isInteger(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  };
  const withFamily = (json: string, f: { id: number; description: string } | null): string => {
    let attrs: Record<string, unknown> = {};
    try {
      attrs = (JSON.parse(json || '{}') as Record<string, unknown>) ?? {};
    } catch {
      return json; // unparseable JSON is the operator's to fix; never overwrite it
    }
    if (f === null) {
      delete attrs.generic_article;
      delete attrs.generic_article_name;
    } else {
      attrs.generic_article = f.id;
      if (f.description) attrs.generic_article_name = f.description;
      else delete attrs.generic_article_name;
    }
    return JSON.stringify(attrs, null, 2);
  };

  const dirty =
    form.brand !== (p.brandRaw ?? p.brandNorm) ||
    form.article !== (p.articleRaw ?? p.articleNorm) ||
    form.name !== (p.name ?? '') ||
    form.attrs.trim() !== JSON.stringify(p.attrs ?? {}, null, 2);

  async function save() {
    setErr('');
    setSaved(false);
    let attrs: Record<string, unknown> = {};
    if (form.attrs.trim()) {
      try {
        attrs = JSON.parse(form.attrs) as Record<string, unknown>;
      } catch {
        setErr('Attributes must be valid JSON, e.g. {"ean":"401234567890","volt":"12"}');
        return;
      }
    }
    setBusy(true);
    const r = await request('PUT', `/v1/admin/internal/${p.id}`, {
      ...(managed ? {} : { brand: form.brand, article: form.article }),
      name: form.name.trim() ? form.name : null,
      attrs,
    });
    setBusy(false);
    if (r.status >= 400) {
      setErr(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    setSaved(true);
    onChange();
  }

  return (
    <div className="blk mb-3.5 p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[13px] font-semibold">Details</span>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Switch
            on={p.enabled}
            onChange={async (v) => {
              await request('PUT', `/v1/admin/internal/${p.id}`, { enabled: v });
              onChange();
            }}
          />
          <span className="text-[12.5px] text-muted">{p.enabled ? 'Injected' : 'Disabled — injected nowhere'}</span>
        </div>
      </div>

      {err && <ErrorBox>{err}</ErrorBox>}

      <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <Field label="Brand">
          <input
            className="input mono"
            value={form.brand}
            readOnly={managed}
            title={managed ? "The catalogue's code — a managed product keeps it" : undefined}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
          />
        </Field>
        <Field label="Article number">
          <input
            className="input mono"
            value={form.article}
            readOnly={managed}
            title={managed ? "The catalogue's code — a managed product keeps it" : undefined}
            onChange={(e) => setForm({ ...form, article: e.target.value })}
          />
        </Field>
      </div>
      <div className="mt-2.5">
        <Field label="Name">
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
      </div>
      <div className="mt-2.5">
        <Field label="Part family">
          <FamilyPicker
            value={familyOf(form.attrs)}
            onPick={(f) => setForm({ ...form, attrs: withFamily(form.attrs, f) })}
          />
        </Field>
      </div>

      <div className="mt-2.5">
        <Field label="Attributes (JSON)">
          <textarea
            className="input mono"
            rows={4}
            spellCheck={false}
            value={form.attrs}
            onChange={(e) => setForm({ ...form, attrs: e.target.value })}
          />
        </Field>
        <div className="mt-1 text-[11.5px] text-faint">
          Anything you want to carry: EAN, dimensions, notes, a supplier reference. Stored verbatim and returned with
          the product.
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button className="btn btn-sm btn-primary" disabled={busy || !dirty} onClick={save}>
          Save changes
        </button>
        {saved && !dirty && <span className="text-[12px] text-ok">Saved.</span>}
        <div className="flex-1" />
        <span className="text-[11.5px] text-faint">
          {managed
            ? "Managed: the code is the catalogue's. What you add here rides on top of its data and is injected."
            : 'Changing the brand or article rewrites the identity — the old one is dropped unless a catalog has seen it.'}
        </span>
      </div>
    </div>
  );
}

/** One row of the per-catalog code table: display, or edit in place. */
function AliasRow({
  alias,
  onChange,
  onError,
}: {
  alias: ProductAlias;
  onChange: () => void;
  onError: (e: string) => void;
}) {
  const { request } = useApi();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    brandRaw: alias.brandRaw,
    articleRaw: alias.articleRaw,
    sourceId: alias.sourceId ?? '',
    extra: alias.extra ? JSON.stringify(alias.extra) : '',
  });

  async function save() {
    let extra: Record<string, unknown> | null = null;
    if (form.extra.trim()) {
      try {
        extra = JSON.parse(form.extra) as Record<string, unknown>;
      } catch {
        onError('Extra fields must be valid JSON, e.g. {"CodiceListino":"999"}');
        return;
      }
    }
    const r = await request('PUT', `/v1/admin/internal/aliases/${alias.id}`, {
      brandRaw: form.brandRaw,
      articleRaw: form.articleRaw,
      sourceId: form.sourceId.trim() ? form.sourceId : null,
      extra,
    });
    if (r.status >= 400) {
      onError(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    setEditing(false);
    onChange();
  }

  async function remove() {
    const r = await request('DELETE', `/v1/admin/internal/aliases/${alias.id}`);
    if (r.status >= 400) {
      onError(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    onChange();
  }

  if (!editing) {
    return (
      <tr>
        <td>
          <SourceChip source={alias.source} />
        </td>
        <td className="mono">{alias.brandRaw}</td>
        <td className="mono">{alias.articleRaw}</td>
        <td className="mono">{alias.sourceId ?? '—'}</td>
        <td className="mono text-faint">{alias.extra ? JSON.stringify(alias.extra) : '—'}</td>
        <td>
          <div className="flex gap-1.5">
            <button className="btn btn-sm" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button className="btn btn-sm btn-danger" onClick={remove}>
              Remove
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <SourceChip source={alias.source} />
      </td>
      <td>
        <input
          className="input mono"
          value={form.brandRaw}
          onChange={(e) => setForm({ ...form, brandRaw: e.target.value })}
        />
      </td>
      <td>
        <input
          className="input mono"
          value={form.articleRaw}
          onChange={(e) => setForm({ ...form, articleRaw: e.target.value })}
        />
      </td>
      <td>
        <input
          className="input mono"
          value={form.sourceId}
          onChange={(e) => setForm({ ...form, sourceId: e.target.value })}
        />
      </td>
      <td>
        <input
          className="input mono"
          value={form.extra}
          onChange={(e) => setForm({ ...form, extra: e.target.value })}
        />
      </td>
      <td>
        <div className="flex gap-1.5">
          <button className="btn btn-sm btn-primary" onClick={save}>
            Save
          </button>
          <button className="btn btn-sm" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Editing panel for an internal product: the codes it pretends to have in each
 * catalog, the OEs it claims, and the products it declares itself equivalent to.
 *
 * These three things are what make injection work — without an alias for a
 * source, the product simply never appears in that source's results.
 */
function InternalEditor({ view, onChange }: { view: ProductView; onChange: () => void }) {
  const { request } = useApi();
  const id = view.product.id;
  const [err, setErr] = useState('');
  const [alias, setAlias] = useState({ source: 'kromeda', brandRaw: '', articleRaw: '', sourceId: '', extra: '' });
  const [oe, setOe] = useState('');
  const [cross, setCross] = useState<{ brand: string; article: string; relation: CrossRelation }>({
    brand: '',
    article: '',
    relation: 'equivalent',
  });

  async function call(method: 'POST' | 'DELETE', path: string, body?: unknown) {
    setErr('');
    const r = await request(method, path, body);
    if (r.status >= 400) {
      setErr(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return false;
    }
    onChange();
    return true;
  }

  async function addAlias() {
    let extra: Record<string, unknown> | null = null;
    if (alias.extra.trim()) {
      try {
        extra = JSON.parse(alias.extra) as Record<string, unknown>;
      } catch {
        setErr('Extra fields must be valid JSON, e.g. {"CodiceListino":"999"}');
        return;
      }
    }
    const ok = await call('POST', `/v1/admin/internal/${id}/aliases`, {
      source: alias.source,
      brandRaw: alias.brandRaw,
      articleRaw: alias.articleRaw,
      ...(alias.sourceId ? { sourceId: alias.sourceId } : {}),
      ...(extra ? { extra } : {}),
    });
    if (ok) setAlias({ source: alias.source, brandRaw: '', articleRaw: '', sourceId: '', extra: '' });
  }

  const declared = view.cross.filter((c) => c.sources.some((s) => s.source === 'internal'));

  return (
    <div className="blk mb-3.5 p-4">
      <div className="mb-1 text-[13px] font-semibold">Internal catalog</div>
      <p className="mb-3 text-[11.5px] text-faint">
        This product is ours. It appears in a database's results only if it has a code for that database below.
      </p>
      {err && <ErrorBox>{err}</ErrorBox>}

      {/* Aliases — the invented per-catalog codes. */}
      <div className="mb-1 text-[11px] uppercase tracking-wide text-faint">Codes per database</div>
      <Table cols={['Database', 'Brand (as it appears)', 'Code (as it appears)', 'Source id', 'Extra fields', '']}>
        {view.aliases.map((a) => (
          <AliasRow key={a.id} alias={a} onChange={onChange} onError={setErr} />
        ))}
        <tr>
          <td>
            <select
              className="input"
              style={{ width: 'auto' }}
              value={alias.source}
              onChange={(e) => setAlias({ ...alias, source: e.target.value })}
            >
              <option value="kromeda">Kromeda</option>
              <option value="tecdoc">TecDoc</option>
            </select>
          </td>
          <td>
            <input
              className="input mono"
              placeholder="MEYLER"
              value={alias.brandRaw}
              onChange={(e) => setAlias({ ...alias, brandRaw: e.target.value })}
            />
          </td>
          <td>
            <input
              className="input mono"
              placeholder="MY1234K"
              value={alias.articleRaw}
              onChange={(e) => setAlias({ ...alias, articleRaw: e.target.value })}
            />
          </td>
          <td>
            <input
              className="input mono"
              placeholder="999-MY1234K"
              value={alias.sourceId}
              onChange={(e) => setAlias({ ...alias, sourceId: e.target.value })}
            />
          </td>
          <td>
            <input
              className="input mono"
              placeholder={alias.source === 'kromeda' ? '{"CodiceListino":"999"}' : '{}'}
              value={alias.extra}
              onChange={(e) => setAlias({ ...alias, extra: e.target.value })}
            />
          </td>
          <td>
            <button
              className="btn btn-sm btn-primary"
              disabled={!alias.brandRaw.trim() || !alias.articleRaw.trim()}
              onClick={addAlias}
            >
              Add
            </button>
          </td>
        </tr>
      </Table>

      <div className="mt-4 grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-faint">OE codes it answers to</div>
          <div className="flex flex-wrap gap-1.5">
            {view.oes.length === 0 && <span className="text-[12px] text-faint">none</span>}
            {view.oes.map((o) => (
              <span key={o.code} className="chip border-line2">
                <span className="mono">{o.code}</span>
                <button
                  className="ml-1.5 text-faint hover:text-bad"
                  title="Remove"
                  onClick={() => call('DELETE', `/v1/admin/internal/${id}/oes/${encodeURIComponent(o.code)}`)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-1.5">
            <input
              className="input mono"
              style={{ flex: 1 }}
              placeholder="OE code…"
              value={oe}
              onChange={(e) => setOe(e.target.value)}
            />
            <button
              className="btn btn-sm"
              disabled={!oe.trim()}
              onClick={async () => {
                if (await call('POST', `/v1/admin/internal/${id}/oes`, { code: oe })) setOe('');
              }}
            >
              Add OE
            </button>
          </div>
        </div>

        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-faint">Declared equivalent to</div>
          <div className="flex flex-wrap gap-1.5">
            {declared.length === 0 && <span className="text-[12px] text-faint">none</span>}
            {declared.map((c) => (
              <span key={c.productId} className="chip border-line2" title={RELATION_HINT[c.relation]}>
                <span className="mono">
                  {c.brand} {c.article}
                </span>
                <span className="ml-1 text-faint">{c.relation}</span>
                <button
                  className="ml-1.5 text-faint hover:text-bad"
                  title="Remove"
                  onClick={() => call('DELETE', `/v1/admin/internal/${id}/cross/${c.productId}`)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-1.5">
            <input
              className="input mono"
              style={{ width: 100 }}
              placeholder="Brand"
              value={cross.brand}
              onChange={(e) => setCross({ ...cross, brand: e.target.value })}
            />
            <input
              className="input mono"
              style={{ flex: 1 }}
              placeholder="Article"
              value={cross.article}
              onChange={(e) => setCross({ ...cross, article: e.target.value })}
            />
            <select
              className="input"
              style={{ width: 'auto' }}
              value={cross.relation}
              onChange={(e) => setCross({ ...cross, relation: e.target.value as CrossRelation })}
              title={RELATION_HINT[cross.relation]}
            >
              <option value="equivalent">equivalent</option>
              <option value="interchange">interchange</option>
              <option value="supersedes">supersedes</option>
            </select>
            <button
              className="btn btn-sm"
              disabled={!cross.brand.trim() || !cross.article.trim()}
              onClick={async () => {
                if (await call('POST', `/v1/admin/internal/${id}/cross`, cross)) {
                  setCross({ brand: '', article: '', relation: cross.relation });
                }
              }}
            >
              Link
            </button>
          </div>
          <div className="mt-1 text-[11px] text-faint">
            <b>equivalent</b> makes the two one product for cross purposes: a cross request for either returns the
            other <i>and everything the other crosses with</i>. <b>interchange</b> only adds this product to that one's
            answer.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Product360({ id }: { id: number }) {
  const { request } = useApi();
  const { go, openProduct } = useNav();
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const { data, loading, error, reload } = useResource<ProductView>(`/v1/products/${id}`);

  /** A catalogue product as ours (`mixed`), or back to the catalogue (`observed`). */
  async function manage(managed: boolean, question: string) {
    if (!window.confirm(question)) return;
    setErr('');
    const r = await request('POST', `/v1/admin/products/${id}/manage`, { managed });
    if (r.status >= 400) {
      setErr(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    reload();
  }

  async function split(partId: number) {
    if (!window.confirm('Split this identity into its own product? Nothing is lost — it keeps every fact it carries.')) return;
    setErr('');
    const r = await request('POST', `/v1/admin/products/${id}/split`, { partIds: [partId] });
    if (r.status >= 400) {
      setErr(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    reload();
  }

  // Only the FIRST load takes the page away. Every edit on this screen ends in a
  // reload, and swapping the whole tree for a spinner unmounts each panel — which
  // is why saving a description never showed its own "Saved.": the component that
  // was about to say so had just been destroyed and rebuilt with fresh state.
  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorBox>{error ?? 'Product not found.'}</ErrorBox>;

  const p = data.product;
  const match = (s: Provenance[]) => !filter || s.some((x) => x.source === filter);
  const oes = data.oes.filter((o) => match(o.sources));
  const cross = data.cross.filter((c) => match(c.sources));
  const allSources = [...new Set(data.identities.flatMap((i) => i.sources.map((s) => s.source)))];
  const inherited = data.cross.filter((c) => c.via.length > 0).length;

  return (
    <>
      <Toolbar>
        <button className="btn btn-sm" onClick={() => go('products')}>
          ← Products
        </button>
        <div className="flex-1" />
        {filter && (
          <>
            <span className="text-[12.5px] text-muted">
              Showing only facts from <b>{filter}</b>
            </span>
            <button className="btn btn-sm" onClick={() => setFilter('')}>
              Show all sources
            </button>
          </>
        )}
      </Toolbar>

      {err && <ErrorBox>{err}</ErrorBox>}

      <div className="blk mb-3.5 p-4">
        <div className="flex items-baseline gap-2">
          <span className="mono text-[17px] font-semibold">
            {p.brandNorm} {p.articleNorm}
          </span>
          {p.kind === 'internal' && <Chip className="border-accent text-accent">internal</Chip>}
          {p.kind === 'mixed' && <Chip className="border-accent text-accent">managed by us</Chip>}
          {!p.enabled && <Chip className="border-bad text-bad">disabled</Chip>}
          <div className="flex-1" />
          {p.kind === 'observed' && (
            <button
              className="btn btn-sm"
              title="Take this catalogue product under our management"
              onClick={() =>
                manage(
                  true,
                  'Manage this product as ours?\n\nIts catalogue data stays exactly as it is. Ours — name, photos, description, family, OEs, cross — is added on top, and the product is injected wherever the catalogue leaves the answer empty (e.g. a TecDoc detail lookup for a part only Kromeda lists).\n\nYou can release it back to the catalogue at any time.',
                )
              }
            >
              Manage as ours
            </button>
          )}
          {p.kind === 'mixed' && (
            <button
              className="btn btn-sm"
              title="Hand this product back to the catalogue"
              onClick={() =>
                manage(
                  false,
                  'Release this product back to the catalogue?\n\nIt stops being injected. Nothing is deleted: the photos, description and data we added stay on the product.',
                )
              }
            >
              Release to catalog
            </button>
          )}
        </div>
        {p.name && <div className="mt-0.5 text-[13px] text-muted">{p.name}</div>}
        <div className="mt-2.5 border-t border-line pt-2">
          <Row label="Product id">
            <span className="mono">#{p.id}</span>
          </Row>
          <Row label="Seen in">
            <div className="flex gap-1">
              {allSources.length ? allSources.map((s) => <SourceChip key={s} source={s} />) : <span className="text-faint">—</span>}
            </div>
          </Row>
          <Row label="Known since">{fmtTime(p.createdAt)}</Row>
          <Row label="Totals">
            <span className="text-muted">
              {data.identities.length} identit{data.identities.length === 1 ? 'y' : 'ies'} · {data.oes.length} OE
              {data.oes.length === 1 ? '' : 's'} · {data.cross.length} cross · {data.anticross.length} anticross flag
              {data.anticross.length === 1 ? '' : 's'}
            </span>
          </Row>
          {p.kind === 'observed' && Object.keys(p.attrs ?? {}).length > 0 && (
            <Row label="Attributes">
              <span className="mono text-[11.5px] text-muted">{JSON.stringify(p.attrs)}</span>
            </Row>
          )}
        </div>
      </div>

      {(p.kind === 'internal' || p.kind === 'mixed') && <InternalDetails view={data} onChange={reload} />}
      {(p.kind === 'internal' || p.kind === 'mixed') && <InternalEditor view={data} onChange={reload} />}

      {/* Identities — the codes this one product is printed under. */}
      <DataBlock
        title="Identities"
        sub={`${data.identities.length} in this cluster`}
        raw={data.identities}
        right={
          <button className="btn btn-sm" onClick={() => setAdding(true)}>
            + Add identity
          </button>
        }
      >
        <Table cols={['Brand (as printed)', 'Article', 'Name', 'Fused by', 'Seen in', '']}>
          {data.identities.map((i) => (
            <tr key={i.partId}>
              <td className="mono">{i.brandRaw ?? i.brandNorm}</td>
              <td className="mono">{i.articleRaw ?? i.articleNorm}</td>
              <td>{i.name ?? '—'}</td>
              <td>
                <span title={METHOD_HINT[i.method] ?? ''} className="cursor-help border-b border-dotted border-line2">
                  {i.method}
                </span>
              </td>
              <td>
                <Sources sources={i.sources} active={filter} onPick={setFilter} />
              </td>
              <td>
                {data.identities.length > 1 && (
                  <button className="btn btn-sm" onClick={() => split(i.partId)}>
                    Split out
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </DataBlock>

      {/* What we publish about the part, under the single custom key. */}
      <div className="mt-3.5">
        <div className="blk !mb-0">
          <div className="blk-head">
            <h3 className="text-[13px] font-semibold">Our data</h3>
            <span className="text-[11.5px] text-faint">
              published on every response carrying this product, under <code>_cati</code>
            </span>
          </div>
          <div className="p-3.5">
            <ProductCustom
              productId={data.product.id}
              values={(data as unknown as { _cati?: ProductCustomValues })._cati ?? null}
              onChange={reload}
            />
          </div>
        </div>
      </div>

      {/* How often anyone has actually been served this product, and by what. */}
      <div className="mt-3.5">
        <div className="blk !mb-0">
          <div className="blk-head">
            <h3 className="text-[13px] font-semibold">Shown in results</h3>
            <span className="text-[11.5px] text-faint">every search that returned this product</span>
          </div>
          <div className="p-3.5">
            <ProductHits productId={data.product.id} />
          </div>
        </div>
      </div>

      {/* Media — ours first, because ours is the half anyone can change, then
          each source's own. Grouped by origin rather than merged: "TecDoc has
          five photos and a datasheet, Kromeda has nothing, we have two" is the
          question being asked, and a single badged gallery makes it something
          you have to reconstruct by counting. */}
      <div className="mt-3.5">
        <div className="blk !mb-0">
          <div className="blk-head">
            <h3 className="text-[13px] font-semibold">Ours</h3>
            <span className="text-[11.5px] text-faint">published on the articles we inject</span>
          </div>
          <div className="flex flex-col gap-3.5 p-3.5">
            <div>
              <div className="mb-2 text-[11.5px] text-faint">Images — the article&rsquo;s `images[]`</div>
              <ProductImages productId={data.product.id} />
            </div>
            <div>
              <div className="mb-2 text-[11.5px] text-faint">Documents — the article&rsquo;s `pdfs[]`</div>
              <ProductDocuments productId={data.product.id} />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3.5">
        <div className="blk !mb-0">
          <div className="blk-head">
            <h3 className="text-[13px] font-semibold">From the sources</h3>
            <span className="text-[11.5px] text-faint">what each catalogue holds for this part</span>
          </div>
          <div className="p-3.5">
            <SourceAssets productId={data.product.id} />
          </div>
        </div>
      </div>

      {/* OEs — one list, per-fact provenance. */}
      <div className="mt-3.5">
        <DataBlock
          title="OE codes"
          sub={filter ? `${oes.length} of ${data.oes.length} (from ${filter})` : `${data.oes.length} total`}
          raw={data.oes}
        >
          {oes.length === 0 ? (
            <Empty>{filter ? `No OE codes from ${filter}.` : 'No OE codes linked yet.'}</Empty>
          ) : (
            <Table cols={['OE code', 'Stated by', 'Curated', 'Anticross']}>
              {oes.map((o) => (
                <tr key={o.code}>
                  <td className="mono">{o.code}</td>
                  <td>
                    <Sources sources={o.sources} active={filter} onPick={setFilter} />
                  </td>
                  <td>{o.curated ? <Chip className="border-xref text-xref">curated</Chip> : <span className="text-faint">—</span>}</td>
                  <td>{o.flagged ? <Chip className="border-bad text-bad">flagged</Chip> : <span className="text-faint">—</span>}</td>
                </tr>
              ))}
            </Table>
          )}
        </DataBlock>
      </div>

      {/* Cross — observed, then curated. */}
      <div className="mt-3.5">
        <DataBlock
          title="Cross references"
          sub={
            filter
              ? `${cross.length} of ${data.cross.length} (from ${filter})`
              : `${data.cross.length} observed${inherited ? ` · ${inherited} via an equivalence` : ''}`
          }
          raw={{ observed: data.cross, curated: data.curatedCross }}
        >
          {cross.length === 0 ? (
            <Empty>{filter ? `No cross references from ${filter}.` : 'No cross references observed yet.'}</Empty>
          ) : (
            <Table cols={['Brand', 'Article', 'Name', 'Relation', 'Via', 'Stated by', '']}>
              {cross.map((c) => (
                <tr key={`${c.productId}-${c.relation}`}>
                  <td className="mono">{c.brand}</td>
                  <td className="mono">{c.article}</td>
                  <td>
                    {c.name ?? '—'}
                    {c.kind === 'internal' && <Chip className="ml-1.5 border-accent text-accent">internal</Chip>}
                  </td>
                  <td className="text-muted" title={RELATION_HINT[c.relation]}>
                    {c.relation}
                  </td>
                  <td>
                    {c.via.length === 0 ? (
                      <span className="text-faint">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {c.via.map((v) => (
                          <button
                            key={v.productId}
                            className="chip cursor-pointer border-line2 text-[11px] hover:border-accent"
                            title="Inherited through this equivalence — click to open it"
                            onClick={() => openProduct(v.productId)}
                          >
                            <span className="mono">
                              {v.brand} {v.article}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <Sources sources={c.sources} active={filter} onPick={setFilter} />
                  </td>
                  <td>
                    <button className="btn btn-sm" onClick={() => openProduct(c.productId)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </DataBlock>
      </div>

      {data.curatedCross.length > 0 && (
        <div className="mt-3.5">
          <DataBlock title="Curated interchanges" sub={`${data.curatedCross.length} hand-entered`} raw={data.curatedCross}>
            <Table cols={['A', 'B', 'Relation', 'Confidence', 'Reason']}>
              {data.curatedCross.map((x) => (
                <tr key={x.id}>
                  <td className="mono">
                    {x.aBrandNorm} {x.aArticleNorm}
                  </td>
                  <td className="mono">
                    {x.bBrandNorm} {x.bArticleNorm}
                  </td>
                  <td>{x.relation}</td>
                  <td className="num">{x.confidence}</td>
                  <td>{x.reason ?? '—'}</td>
                </tr>
              ))}
            </Table>
          </DataBlock>
        </div>
      )}

      {data.anticross.length > 0 && (
        <div className="mt-3.5">
          <Banner icon="⚠">
            This product is anticross-flagged against {data.anticross.length} OE code
            {data.anticross.length === 1 ? '' : 's'} — a match on {data.anticross.length === 1 ? 'it' : 'them'} is known
            to be wrong.
          </Banner>
          <DataBlock title="Anticross flags" sub={`${data.anticross.length}`} raw={data.anticross}>
            <Table cols={['OE', 'Status', 'Confidence', 'Evidence', 'Reason']}>
              {data.anticross.map((a) => (
                <tr key={a.id}>
                  <td className="mono">{a.oeNorm}</td>
                  <td>
                    <Chip className={a.status === 'confirmed' ? 'border-bad text-bad' : 'border-warn text-warn'}>
                      {a.status}
                    </Chip>
                  </td>
                  <td className="num">{a.confidence}</td>
                  <td className="num">{a.evidenceCount}</td>
                  <td>{a.reason ?? '—'}</td>
                </tr>
              ))}
            </Table>
          </DataBlock>
        </div>
      )}

      {adding && <AddIdentity id={id} onClose={() => setAdding(false)} onDone={reload} />}
    </>
  );
}
